// Triage: the model reports facts, the server decides.
//
// The extract call fills `TopicSignals` per topic: each fact is a verbatim
// quote pinned to one utterance (`segId`). `verifySignals` checks every quote
// against the transcript and drops what fails; `classifyTopic` then decides
// none / ticket / epic from the verified facts alone. No field is the verdict
// in disguise: the number of outcomes is counted here, from verified quotes,
// never taken from the model (precedent: `readiness/sizing.ts`).
//
// Pure, browser-safe: no I/O, no regex lookbehind.

import { z } from "zod";

import { normalizeQuote } from "../i18n.js";

import type { TicketClass, TicketReason, TopicReason } from "./vocabulary.js";

// ---------------------------------------------------------------------------
// The model's output (strict: no record, no passthrough)
// ---------------------------------------------------------------------------

export const QuoteSchema = z
  .object({
    segId: z.string(),
    quote: z.string(),
  })
  .strict();
export type Quote = z.infer<typeof QuoteSchema>;

export const ABOUT_THIS_PROJECT = ["yes", "other_project", "unclear"] as const;
export const SETTLED_KINDS = ["done_before", "done_in_meeting", "rejected"] as const;
export type SettledKind = (typeof SETTLED_KINDS)[number];

export const TopicSignalsSchema = z
  .object({
    /** What the topic is (min-length rule). */
    topicQuote: QuoteSchema,
    aboutThisProject: z.enum(ABOUT_THIS_PROJECT),
    /** The ask for a change to the product or code. */
    changeQuote: QuoteSchema.nullable(),
    /** Each named outcome, verbatim; the server counts them. */
    deliverables: z.array(QuoteSchema),
    /** Uptake: "Mach ich.", "Nehmen wir rein." (no min length). */
    commitmentQuote: QuoteSchema.nullable(),
    /** "man könnte", "irgendwann", "wäre cool" (no min length). */
    hedgeQuote: QuoteSchema.nullable(),
    /** Something was decided. */
    decisionQuote: QuoteSchema.nullable(),
    /** "ist schon erledigt", "machen wir nicht". */
    settledQuote: QuoteSchema.nullable(),
    settledKind: z.enum(SETTLED_KINDS).nullable(),
    /** A progress report on existing work. */
    statusQuote: QuoteSchema.nullable(),
    statusOfIid: z.number().int().nullable(),
  })
  .strict();
export type TopicSignals = z.infer<typeof TopicSignalsSchema>;

// ---------------------------------------------------------------------------
// Verification (code, 6.3)
// ---------------------------------------------------------------------------

/** A quote of the "what was said" kind needs at least this much text. */
export const MIN_QUOTE_WORDS = 4;
export const MIN_QUOTE_CHARS = 20;

/** One aligned utterance `u###`, in transcript order. */
export interface TriageUtterance {
  readonly segId: string;
  readonly text: string;
}

/** A topic's span of utterances, by `segId`, inclusive. */
export interface SegRange {
  readonly from: string;
  readonly to: string;
}

export interface VerifyContext {
  /** The whole meeting's utterances, in transcript order. */
  readonly utterances: readonly TriageUtterance[];
  /** The topic's ranges; a quote may sit one utterance outside a range. */
  readonly segRange: SegRange | readonly SegRange[];
  /** Open issue iids from the project snapshot. */
  readonly openIids: ReadonlySet<number> | readonly number[];
}

/** `TopicSignals` after verification: a failed quote is `null` (or dropped
 *  from `deliverables`), and dependent facts fall back with it. */
export interface VerifiedSignals {
  readonly topicQuote: Quote | null;
  readonly aboutThisProject: TopicSignals["aboutThisProject"];
  readonly changeQuote: Quote | null;
  readonly deliverables: readonly Quote[];
  readonly commitmentQuote: Quote | null;
  readonly hedgeQuote: Quote | null;
  readonly decisionQuote: Quote | null;
  readonly settledQuote: Quote | null;
  readonly settledKind: SettledKind | null;
  readonly statusQuote: Quote | null;
  readonly statusOfIid: number | null;
}

const wordCount = (s: string): number => (s === "" ? 0 : s.split(" ").length);

/**
 * Verify every quote of one topic against the transcript (6.3):
 * - its `segId` exists and lies inside one of the topic's ranges, ± one
 *   utterance;
 * - "what was said" quotes (topic, change, deliverables, decision, settled,
 *   status) are a substring of that one utterance after `normalizeQuote`, with
 *   at least `MIN_QUOTE_WORDS` words and `MIN_QUOTE_CHARS` characters;
 * - commitment and hedge are an exact substring of that utterance (only
 *   whitespace runs fold to one space) with no minimum: "Mach ich." is a
 *   commitment because the `segId` pins it down.
 * `settledKind` survives only with `settledQuote`; `statusOfIid` only when it
 * is an open iid in the snapshot.
 */
export const verifySignals = (signals: TopicSignals, ctx: VerifyContext): VerifiedSignals => {
  const indexOf = new Map<string, number>();
  ctx.utterances.forEach((u, i) => {
    if (!indexOf.has(u.segId)) indexOf.set(u.segId, i);
  });
  const ranges: readonly SegRange[] = Array.isArray(ctx.segRange)
    ? (ctx.segRange as readonly SegRange[])
    : [ctx.segRange as SegRange];
  const bounds = ranges.flatMap((r) => {
    const a = indexOf.get(r.from);
    const b = indexOf.get(r.to);
    if (a === undefined || b === undefined) return [];
    return [{ lo: Math.min(a, b) - 1, hi: Math.max(a, b) + 1 }];
  });

  /** The utterance a quote cites, when it exists and sits in range. */
  const citedText = (q: Quote): string | null => {
    const i = indexOf.get(q.segId);
    if (i === undefined) return null;
    if (!bounds.some((b) => i >= b.lo && i <= b.hi)) return null;
    return ctx.utterances[i]!.text;
  };

  const strict = (q: Quote | null): Quote | null => {
    if (q === null) return null;
    const text = citedText(q);
    if (text === null) return null;
    const nq = normalizeQuote(q.quote);
    if (nq.length < MIN_QUOTE_CHARS || wordCount(nq) < MIN_QUOTE_WORDS) return null;
    return normalizeQuote(text).includes(nq) ? q : null;
  };

  // Exact except for whitespace: the prompt shows each utterance with its
  // whitespace runs collapsed (`renderMeetingLine`), so the model can only
  // quote the collapsed form. Case and punctuation stay byte-exact.
  const ws = (s: string): string => s.replace(/\s+/gu, " ").trim().normalize("NFC");
  const exact = (q: Quote | null): Quote | null => {
    if (q === null) return null;
    const text = citedText(q);
    if (text === null) return null;
    const nq = ws(q.quote);
    if (nq === "") return null;
    return ws(text).includes(nq) ? q : null;
  };

  const open =
    ctx.openIids instanceof Set
      ? (ctx.openIids as ReadonlySet<number>)
      : new Set(ctx.openIids as readonly number[]);
  const settledQuote = strict(signals.settledQuote);

  return {
    topicQuote: strict(signals.topicQuote),
    aboutThisProject: signals.aboutThisProject,
    changeQuote: strict(signals.changeQuote),
    deliverables: signals.deliverables.flatMap((d) => {
      const v = strict(d);
      return v === null ? [] : [v];
    }),
    commitmentQuote: exact(signals.commitmentQuote),
    hedgeQuote: exact(signals.hedgeQuote),
    decisionQuote: strict(signals.decisionQuote),
    settledQuote,
    settledKind: settledQuote === null ? null : signals.settledKind,
    statusQuote: strict(signals.statusQuote),
    statusOfIid:
      signals.statusOfIid !== null && open.has(signals.statusOfIid) ? signals.statusOfIid : null,
  };
};

/** Verified named outcomes, counted once per utterance. */
export const deliverableCount = (signals: Pick<VerifiedSignals, "deliverables">): number =>
  new Set(signals.deliverables.map((d) => d.segId)).size;

// ---------------------------------------------------------------------------
// Classification (6.4), evaluated in order
// ---------------------------------------------------------------------------

export type TopicClass = "none" | TicketClass;

export type Classification =
  | { readonly class: "none"; readonly reason: TopicReason }
  | { readonly class: TicketClass; readonly reason: TicketReason };

/**
 * Decide a topic's class from its VERIFIED signals. Rules 0–10 of the design,
 * first match wins:
 *  0 no topic quote → none/ungrounded
 *  1 other project → none/other_project
 *  2 unclear project → none/unclear_project
 *  3 settled → none/already_settled
 *  4 status report and no change → none/status_update
 *  5 no change, a decision → none/decision_no_work
 *  6 no change → none/chatter
 *  7 two or more outcomes → epic/too_broad
 *  8 commitment and one outcome → ticket/committed (beats a hedge)
 *  9 hedged, or no outcome → epic/idea
 * 10 otherwise → ticket/requested
 */
export const classifyTopic = (v: VerifiedSignals): Classification => {
  if (v.topicQuote === null) return { class: "none", reason: "ungrounded" };
  if (v.aboutThisProject === "other_project") return { class: "none", reason: "other_project" };
  if (v.aboutThisProject === "unclear") return { class: "none", reason: "unclear_project" };
  if (v.settledQuote !== null) return { class: "none", reason: "already_settled" };
  if (v.statusQuote !== null && v.changeQuote === null) {
    return { class: "none", reason: "status_update" };
  }
  if (v.changeQuote === null && v.decisionQuote !== null) {
    return { class: "none", reason: "decision_no_work" };
  }
  if (v.changeQuote === null) return { class: "none", reason: "chatter" };
  const outcomes = deliverableCount(v);
  if (outcomes >= 2) return { class: "epic", reason: "too_broad" };
  if (v.commitmentQuote !== null && outcomes === 1) return { class: "ticket", reason: "committed" };
  if (v.hedgeQuote !== null || outcomes === 0) return { class: "epic", reason: "idea" };
  return { class: "ticket", reason: "requested" };
};
